import React from 'react';
import { Form, FormGroup, FormControl, Button, Card } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import config from '../../../config';
import { apiFetch } from '../../_services/apiFetch';
import { canAccessQCModule } from '../../utils/roleConstants';
import ApprovalLevelApprovers from '../../components/ApprovalLevelApprovers';
import {
    groupApprovalLevelsFromApi,
    serializeApprovalLevelsForApi,
    emptyApprovalLevel,
} from '../../utils/approvalRoutingUtils';

class ApprovalRouting extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            approvalLevels: [],
            users: [],
            loading: false,
            selectedModule: 'QC',
        };
    }

    componentWillMount() {
        // Check QC admin access
        try {
            const userData = localStorage.getItem('user');
            if (!userData) {
                return;
            }
            const user = JSON.parse(userData);
            if (!canAccessQCModule(user)) {
                return;
            }
        } catch (error) {
            console.error('Error checking QC admin access:', error);
            return;
        }
        this.fetchUsers();
        this.fetchApprovalRouting();
    }

    fetchUsers = () => {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        };

        if (localStorage.getItem('user') != null) {
            const accountId = JSON.parse(localStorage.getItem('user')).accountId;
            fetch(`${config.apiUrl}/getApprovers?account_id=${accountId}`, requestOptions)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text().then(text => {
                        if (!text || text.trim() === '') {
                            return [];
                        }
                        try {
                            const data = JSON.parse(text);
                            return Array.isArray(data) ? data : [];
                        } catch (e) {
                            console.error('Error parsing JSON:', e);
                            return [];
                        }
                    });
                })
                .then(users => {
                    if (users && Array.isArray(users)) {
                        this.setState({ users: users });
                    }
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                    // Don't show alert, just log the error
                });
        }
    }

    fetchApprovalRouting = () => {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        };

        if (localStorage.getItem('user') != null) {
            const accountId = JSON.parse(localStorage.getItem('user')).accountId;
            const module = this.state.selectedModule || 'QC';
            let url = `${config.apiUrl}/getApprovalRouting?account_id=${accountId}&module=${encodeURIComponent(module)}`;

            if (module === 'Sales') {
                url = `${config.apiUrl}/getSalesApprovalRouting?account_id=${accountId}`;
            } else if (module === 'Purchasing') {
                url = `${config.apiUrl}/getPurchaseApprovalRouting?account_id=${accountId}`;
            } else if (module === 'Finance & Accounting') {
                url = `${config.apiUrl}/getAccountingApprovalRouting?account_id=${accountId}`;
            }

            apiFetch(url, requestOptions)
                .then((response) => {
                    if (!response.ok) {
                        // If 404 or other error, return empty array
                        if (response.status === 404) {
                            return [];
                        }
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text().then(text => {
                        if (!text || text.trim() === '') {
                            return [];
                        }
                        try {
                            const data = JSON.parse(text);
                            return Array.isArray(data) ? data : [];
                        } catch (e) {
                            console.error('Error parsing JSON:', e);
                            return [];
                        }
                    });
                })
                .then(data => {
                    const grouped = groupApprovalLevelsFromApi(data);
                    this.setState({
                        approvalLevels: grouped.length > 0 ? grouped : [emptyApprovalLevel(1)],
                    });
                })
                .catch(error => {
                    console.error('Error fetching approval routing:', error);
                    this.setState({ approvalLevels: [emptyApprovalLevel(1)] });
                });
        } else {
            this.setState({ approvalLevels: [emptyApprovalLevel(1)] });
        }
    }

    addApprovalLevel = () => {
        const newLevel = this.state.approvalLevels.length + 1;
        this.setState({
            approvalLevels: [...this.state.approvalLevels, emptyApprovalLevel(newLevel)],
        });
    }

    removeApprovalLevel = (index) => {
        const newLevels = this.state.approvalLevels.filter((_, i) => i !== index);
        // Reorder levels
        const reorderedLevels = newLevels.map((level, idx) => ({
            ...level,
            level: idx + 1
        }));
        this.setState({ approvalLevels: reorderedLevels });
    }

    toggleApprover = (index, approverId) => {
        const newLevels = [...this.state.approvalLevels];
        const current = newLevels[index].approverIds || [];
        const value = String(approverId);
        const approverIds = current.map(String).includes(value)
            ? current.filter((id) => String(id) !== value)
            : [...current.map(String), value];
        newLevels[index] = { ...newLevels[index], approverIds };
        this.setState({ approvalLevels: newLevels });
    }

    handleSave = () => {
        if (localStorage.getItem('user') == null) {
            window.alert('User not logged in');
            return;
        }

        const accountId = JSON.parse(localStorage.getItem('user')).accountId;
        const levels = Array.isArray(this.state.approvalLevels) ? this.state.approvalLevels : [];

        const emptyLevels = levels.filter((level) => !(level.approverIds || []).length);
        if (emptyLevels.length > 0) {
            window.alert('Please select at least one approver for each level');
            return;
        }

        const requestOptions = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                account_id: accountId,
                approval_levels: serializeApprovalLevelsForApi(levels),
                module: this.state.selectedModule || 'QC',
            })
        };

        this.setState({ loading: true });

        let url = `${config.apiUrl}/saveApprovalRouting`;
        if (this.state.selectedModule === 'Sales') {
            url = `${config.apiUrl}/saveSalesApprovalRouting`;
        } else if (this.state.selectedModule === 'Purchasing') {
            url = `${config.apiUrl}/savePurchaseApprovalRouting`;
        } else if (this.state.selectedModule === 'Finance & Accounting') {
            url = `${config.apiUrl}/saveAccountingApprovalRouting`;
        }

        apiFetch(url, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    let data;
                    try {
                        data = text ? JSON.parse(text) : {};
                    } catch (e) {
                        data = {};
                    }

                    if (response.status === 200) {
                        window.alert('Approval routing saved successfully');
                        // Refresh the routing data after successful save
                        this.fetchApprovalRouting();
                        this.setState({ loading: false });
                    } else {
                        const errorMsg = data.error || 'Failed to save approval routing';
                        window.alert(errorMsg);
                        this.setState({ loading: false });
                    }
                });
            })
            .catch(error => {
                console.error('Error saving approval routing:', error);
                window.alert('Failed to save approval routing. Please check your connection and try again.');
                this.setState({ loading: false });
            });
    }

    render() {
        // Check QC admin access
        try {
            const userData = localStorage.getItem('user');
            if (!userData) {
                return <Navigate to="/login" replace />;
            }
            const user = JSON.parse(userData);
            if (!canAccessQCModule(user)) {
                return (
                    <div className="container">
                        <div style={{ marginTop: '20px', padding: '20px', textAlign: 'center' }}>
                            <h4>Access Denied</h4>
                            <p>You do not have permission to access Approval Routing. This feature is only available for administrators.</p>
                        </div>
                    </div>
                );
            }
        } catch (error) {
            console.error('Error checking user access:', error);
            return <Navigate to="/login" replace />;
        }

        const { approvalLevels, users, selectedModule } = this.state;

        const levels = Array.isArray(approvalLevels) ? approvalLevels : [];
        const userList = Array.isArray(users) ? users : [];
        let moduleLabel = 'Q-Value';
        let moduleDesc = ' (incoming, outgoing, in-progress, FPBO, LPBO, FAI)';
        switch (selectedModule) {
            case 'QC':
                moduleLabel = 'Q-Value';
                moduleDesc = ' (incoming, outgoing, in-progress, FPBO, LPBO, FAI)';
                break;
            case 'inventory_management':
                moduleLabel = 'Inventory Management';
                moduleDesc = ' (scrap / variance approvals in the mobile app)';
                break;
            case 'Sales':
                moduleLabel = 'Sales';
                moduleDesc = ' (quotations, sales orders, invoices, delivery orders)';
                break;
            case 'Purchasing':
                moduleLabel = 'Purchasing';
                moduleDesc = ' (purchase orders)';
                break;
            case 'Finance & Accounting':
                moduleLabel = 'Finance & Accounting';
                moduleDesc = ' (purchase invoice, payment vouchers, credit notes, debit notes)';
                break;
            case 'Tooling':
                moduleLabel = 'Tooling';
                moduleDesc = ' (Tooling Maintenance Process approvals)';
                break;
        }

        return (
            <div className="container">
                <div style={{ marginBottom: '20px' }}>
                    <h4>Approval Routing Settings</h4>
                    <p>Configure multiple approval levels for your workflow. You can assign multiple approvers per level; any one of them can approve, and the others will no longer see the item as pending.</p>
                    <FormGroup style={{ maxWidth: '420px', marginTop: '16px' }}>
                        <Form.Label>Module</Form.Label>
                        <FormControl
                            as="select"
                            value={selectedModule}
                            disabled={this.state.loading}
                            onChange={(e) => {
                                const nextModule = e.target.value;
                                this.setState(
                                    { selectedModule: nextModule, approvalLevels: [emptyApprovalLevel(1)] },
                                    () => this.fetchApprovalRouting()
                                );
                            }}
                        >
                            <option value="QC">Q-Value</option>
                            <option value="inventory_management">Inventory Management</option>
                            <option value="Tooling">Tooling</option>
                            <option value="Sales">Sales</option>
                            <option value="Purchasing">Purchasing</option>
                            <option value="Finance & Accounting">Finance & Accounting</option>
                        </FormControl>
                        <small className="text-muted" style={{ display: 'block', marginTop: '6px' }}>
                            Editing routing for: <strong>{moduleLabel}</strong>
                            {moduleDesc}
                        </small>
                    </FormGroup>

                    {levels.length === 0 && (
                        <div className="alert alert-info" style={{ marginTop: '10px' }}>
                            <strong>No approval routing configured.</strong> Add approval levels to require sequential approvals for new records.
                        </div>
                    )}
                </div>

                {/* Approval Levels Cards */}
                <div style={{ marginBottom: '20px' }}>
                    {levels.map((level, index) => (
                        <Card key={index} style={{ marginBottom: '15px' }}>
                            <Card.Body>
                                <ApprovalLevelApprovers
                                    level={level}
                                    approverIds={level.approverIds || []}
                                    users={userList}
                                    loading={this.state.loading}
                                    canRemove={levels.length > 1}
                                    onToggleApprover={(approverId) => this.toggleApprover(index, approverId)}
                                    onRemoveLevel={() => this.removeApprovalLevel(index)}
                                />
                            </Card.Body>
                        </Card>
                    ))}
                </div>

                {/* Action Buttons */}
                <div style={{ marginBottom: '20px' }}>
                    <Button
                        variant="primary"
                        onClick={this.addApprovalLevel}
                        disabled={this.state.loading}
                    >
                        + Add Approval Level
                    </Button>
                </div>

                <div>
                    <Button
                        variant="secondary"
                        onClick={() => this.fetchApprovalRouting()}
                        style={{ marginRight: '10px' }}
                        disabled={this.state.loading}
                    >
                        Cancel/Refresh
                    </Button>
                    <Button
                        variant="primary"
                        onClick={this.handleSave}
                        disabled={this.state.loading}
                    >
                        {this.state.loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        );
    }
}

export { ApprovalRouting };